import React from 'react';
import type { MarketingPlan } from '../../../context/TenantContext';
import { useTenant } from '../../../context/TenantContext';
import { parseEm } from '../inlineEm';
import { PRICING, SCHOOL_IEB, SCHOOL_NSC } from './content';
import { ReviseSchoolLicence } from './SchoolLicence';
import { REVISE_ANCHORS } from './sectionMeta';

/** Split plans into rows by `group`, preserving first-appearance order. Plans
 *  with no group fall into one trailing row, which is what a theme that has
 *  never used grouping (utility) produces. */
/** A plan as this component reads it. Structurally `MarketingPlan`, but with
 *  `features` readonly so a page can hand over an `as const` literal — which
 *  IEB_PAGE.pricing is, deliberately, so its figures cannot be edited by
 *  accident. */
export type RevisePlan = Omit<MarketingPlan, 'features'> & { features: readonly string[] };

const groupPlans = (plans: readonly RevisePlan[]): RevisePlan[][] => {
  const order: string[] = [];
  const rows = new Map<string, RevisePlan[]>();
  for (const plan of plans) {
    const key = plan.group ?? '';
    if (!rows.has(key)) {
      rows.set(key, []);
      order.push(key);
    }
    rows.get(key)!.push(plan);
  }
  return order.map((key) => rows.get(key)!);
};

/** Pricing shown on a page that brings its own, bypassing the tenant's.
 *  `/ieb` uses this: its prices are the IEB's, not the NSC's, and like the
 *  rest of IEB_PAGE they are deliberately not `branding.marketing`-editable
 *  — the figures have to agree with a printed flyer, and field-by-field
 *  overrides are how a set price drifts away from the parts total under it. */
interface RevisePricingProps {
  pricing?: {
    heading?: string;
    subhead?: string;
    plans: readonly RevisePlan[];
    /** One quiet line under the tiers: access length, refund terms. */
    note?: string;
  };
  /** Anchor id for a page that brings its own pricing, so the masthead can
   *  link to it the way the landing page links to REVISE_ANCHORS.pricing. */
  anchor?: string;
}

export const RevisePricing: React.FC<RevisePricingProps> = ({ pricing: override, anchor }) => {
  /* The school licence belongs to whichever board's prices these are: an
     override means /ieb (one flat price), otherwise the NSC's fee bands.
     It renders inside this section rather than beside it because it answers
     the same question the tiers do — what does this cost — for the other
     kind of buyer. */
  const school = override ? SCHOOL_IEB : SCHOOL_NSC;
  const { tenant } = useTenant();
  const tenantPricing = override ? undefined : tenant?.branding.marketing?.pricing;
  const base = override ?? PRICING;
  const plans = tenantPricing?.plans?.length ? tenantPricing.plans : base.plans;
  const heading = tenantPricing?.heading || base.heading || '';
  const subhead = tenantPricing?.subhead || base.subhead;
  const note = override?.note;

  if (plans.length === 0) return null;

  return (
    <section className="rv-sec" id={override ? anchor : REVISE_ANCHORS.pricing} data-accent="essay">
      <div className="rv-wrap">
        <div className="rv-head">
          <p className="rv-kicker">Pricing</p>
          {heading && <h2>{parseEm(heading)}</h2>}
          {subhead && <p className="rv-lede">{subhead}</p>}
        </div>

        {groupPlans(plans).map((row, i) => (
          <React.Fragment key={row[0].group || `row-${i}`}>
            {/* The label belongs to the row, so it comes from the first plan in
                the group; later ones are ignored rather than fighting over it. */}
            {row[0].groupLabel && <p className="rv-buyway">{row[0].groupLabel}</p>}
            <div className="rv-tiers">
            {row.map((plan) => (
              <div className={`rv-tier${plan.featured ? ' rv-tier-featured' : ''}`} key={plan.name}>
                <div className="rv-trow-price">
                  <span className="rv-name">{plan.name}</span>
                  <span className="rv-price">{plan.price}</span>
                </div>
                {plan.tagline && <p>{plan.tagline}</p>}
                {plan.features.length > 0 && (
                  <ul>
                    {plan.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                )}
                {plan.badge && <span className="rv-badge">{plan.badge}</span>}
                {plan.ctaLabel && plan.ctaHref && (
                  <a className="rv-btn rv-btn-ghost" href={plan.ctaHref}>
                    {plan.ctaLabel}
                  </a>
                )}
              </div>
            ))}
            </div>
          </React.Fragment>
        ))}

        {note && <p className="rv-price-note">{note}</p>}

        <ReviseSchoolLicence copy={school} />
      </div>
    </section>
  );
};
